
import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography
} from '@mui/material'

import { fetchWithAuth } from '../utils/fetchWithAuth'

const AdminCountries = () => {
  const [countries, setCountries] = useState([])
  const [loading, setLoading] = useState(false)
  const [savingField, setSavingField] = useState(null)
  const [bulkSaving, setBulkSaving] = useState(null)
  const [error, setError] = useState('')
  const [vatDrafts, setVatDrafts] = useState({})

  const loadCountries = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await fetchWithAuth('/admin/countries')
      const data = await response.json()

      if (!response.ok) {
        throw new Error(
          data?.message ||
          'Nepodarilo sa načítať krajiny.'
        )
      }

      setCountries(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      setError(
        error.message ||
        'Nepodarilo sa načítať krajiny.'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCountries()
  }, [loadCountries])

  const changeCountryStatus = async (
    country,
    field
  ) => {
    const value = !country[field]
    const savingKey = `${country.code}-${field}`

    setSavingField(savingKey)
    setError('')

    try {
      const response = await fetchWithAuth(
        `/admin/countries/${country.code}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            [field]: value
          })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(
          data?.message ||
          'Nepodarilo sa upraviť krajinu.'
        )
      }

      setCountries(current =>
        current.map(item =>
          item.code === country.code
            ? data
            : item
        )
      )
    } catch (error) {
      setError(
        error.message ||
        'Nepodarilo sa upraviť krajinu.'
      )
    } finally {
      setSavingField(null)
    }
  }

  const saveVatRate = async country => {
    const raw = String(
      vatDrafts[country.code] ??
      country.vat_rate ??
      ''
    )
      .trim()
      .replace(',', '.')

    if (
      raw !== '' &&
      (
        !/^\d+(\.\d{1,2})?$/.test(raw) ||
        Number(raw) > 100
      )
    ) {
      setError(
        'DPH musí byť číslo od 0 do 100 s maximálne dvoma desatinnými miestami.'
      )
      return
    }

    const value =
      raw === ''
        ? null
        : Number(raw)

    const savingKey = `${country.code}-vat_rate`

    setSavingField(savingKey)
    setError('')

    try {
      const response = await fetchWithAuth(
        `/admin/countries/${country.code}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            vat_rate: value
          })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(
          data?.message ||
          'Nepodarilo sa uložiť DPH.'
        )
      }

      setCountries(current =>
        current.map(item =>
          item.code === country.code
            ? data
            : item
        )
      )

      setVatDrafts(current => {
        const next = { ...current }
        delete next[country.code]
        return next
      })
    } catch (error) {
      setError(
        error.message ||
        'Nepodarilo sa uložiť DPH.'
      )
    } finally {
      setSavingField(null)
    }
  }

  const bulkUpdate = async (
    field,
    value
  ) => {
    const bulkKey = `${field}-${value}`

    setBulkSaving(bulkKey)
    setError('')

    try {
      const response = await fetchWithAuth(
        '/admin/countries/bulk',
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            [field]: value
          })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(
          data?.message ||
          'Nepodarilo sa hromadne upraviť krajiny.'
        )
      }

      setCountries(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      setError(
        error.message ||
        'Nepodarilo sa hromadne upraviť krajiny.'
      )
    } finally {
      setBulkSaving(null)
    }
  }

  const disabled =
    savingField !== null ||
    bulkSaving !== null

  const allRegistrationEnabled =
    countries.length > 0 &&
    countries.every(
      country => country.enabled
    )

  const allReceiptUploadEnabled =
    countries.length > 0 &&
    countries.every(
      country =>
        country.receipt_upload_enabled
    )

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          marginBottom: 1
        }}
      >
        <h3
          style={{
            fontSize: 16,
            margin: 0
          }}
        >
          Nastavenie krajín
        </h3>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <FormControlLabel
            label={
              allRegistrationEnabled
                ? 'Registrácia povolená pre všetky'
                : 'Registrácia zakázaná pre všetky'
            }
            labelPlacement="start"
            control={
              <Switch
                size="small"
                checked={allRegistrationEnabled}
                disabled={disabled}
                onChange={() =>
                  bulkUpdate(
                    'enabled',
                    !allRegistrationEnabled
                  )
                }
              />
            }
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: 13
              }
            }}
          />

          <FormControlLabel
            label={
              allReceiptUploadEnabled
                ? 'Upload povolený pre všetky'
                : 'Upload zakázaný pre všetky'
            }
            labelPlacement="start"
            control={
              <Switch
                size="small"
                checked={allReceiptUploadEnabled}
                disabled={disabled}
                onChange={() =>
                  bulkUpdate(
                    'receipt_upload_enabled',
                    !allReceiptUploadEnabled
                  )
                }
              />
            }
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: 13
              }
            }}
          />
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            marginBottom: 1,
            py: 0.5,
            fontSize: 13
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: 3
          }}
        >
          <CircularProgress size={26} />
        </Box>
      ) : (
        <div
          style={{
            border: '1px solid #c7c7c7',
            borderRadius: 10,
            background: '#fff',
            flex: 1,
            minHeight: 0,
            maxWidth: '100%',
            overflow: 'auto'
          }}
        >
          <Box>
            {countries.map((country, index) => {
              const registrationSaving =
                savingField ===
                `${country.code}-enabled`

              const uploadSaving =
                savingField ===
                `${country.code}-receipt_upload_enabled`

              const vatSaving =
                savingField ===
                `${country.code}-vat_rate`

              const vatValue =
                vatDrafts[country.code] ??
                (country.vat_rate ?? '')

              const vatChanged =
                vatDrafts[country.code] !== undefined &&
                String(vatDrafts[country.code])
                  .trim()
                  .replace(',', '.') !==
                String(country.vat_rate ?? '')

              return (
                <Box key={country.code}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      minHeight: 40,
                      gap: 2,
                      px: 1.5
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 160
                      }}
                    >
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontSize: 13
                        }}
                      >
                        {country.code} - {country.name}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          minWidth: 190
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          DPH:
                        </Typography>

                        <TextField
                          size="small"
                          type="text"
                          value={vatValue}
                          inputProps={{
                            inputMode: 'decimal',
                            'aria-label': `DPH ${country.name}`
                          }}
                          onChange={event =>
                            setVatDrafts(current => ({
                              ...current,
                              [country.code]: event.target.value
                            }))
                          }
                          disabled={disabled}
                          sx={{
                            width: 75,
                            '& .MuiInputBase-input': {
                              fontSize: 13,
                              padding: '6px 8px',
                              textAlign: 'right'
                            }
                          }}
                        />

                        <Typography sx={{ fontSize: 13 }}>
                          %
                        </Typography>

                        {vatSaving && (
                          <CircularProgress size={16} />
                        )}

                        <Button
                          size="small"
                          variant="outlined"
                          disabled={
                            disabled ||
                            !vatChanged
                          }
                          onClick={() =>
                            saveVatRate(country)
                          }
                          sx={{
                            fontSize: 12,
                            minWidth: 60,
                            textTransform: 'none'
                          }}
                        >
                          Uložiť
                        </Button>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 190,
                          justifyContent: 'flex-end'
                        }}
                      >
                        {registrationSaving && (
                          <CircularProgress
                            size={16}
                            sx={{
                              marginRight: 0.5
                            }}
                          />
                        )}

                        <FormControlLabel
                          label={
                            country.enabled
                              ? 'Registrácia povolená'
                              : 'Registrácia zakázaná'
                          }
                          labelPlacement="start"
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(
                                country.enabled
                              )}
                              disabled={disabled}
                              onChange={() =>
                                changeCountryStatus(
                                  country,
                                  'enabled'
                                )
                              }
                            />
                          }
                          sx={{
                            margin: 0,
                            '& .MuiFormControlLabel-label': {
                              fontSize: 13
                            }
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 190,
                          justifyContent: 'flex-end'
                        }}
                      >
                        {uploadSaving && (
                          <CircularProgress
                            size={16}
                            sx={{
                              marginRight: 0.5
                            }}
                          />
                        )}

                        <FormControlLabel
                          label={
                            country.receipt_upload_enabled
                              ? 'Upload povolený'
                              : 'Upload zakázaný'
                          }
                          labelPlacement="start"
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(
                                country.receipt_upload_enabled
                              )}
                              disabled={disabled}
                              onChange={() =>
                                changeCountryStatus(
                                  country,
                                  'receipt_upload_enabled'
                                )
                              }
                            />
                          }
                          sx={{
                            margin: 0,
                            '& .MuiFormControlLabel-label': {
                              fontSize: 13
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {index < countries.length - 1 && (
                    <Divider />
                  )}
                </Box>
              )
            })}

            {!countries.length && (
              <Typography
                color="text.secondary"
                sx={{
                  padding: 1.5,
                  fontSize: 13
                }}
              >
                Neboli nájdené žiadne krajiny.
              </Typography>
            )}
          </Box>
        </div>
      )}
    </div>
  )
}

export default AdminCountries
